import axios, { AxiosInstance } from 'axios';
import { 
  ApiResponse, 
  JiraProject
} from '../types';
import type {
  WeeklyMeeting,
  WeeklyMeetingPatch,
  WeeklyMeetingSummary,
} from '../domain/pointHebdoSprint';
import {
  normalizePerformanceReview,
  type PerformanceCycle,
  type PerformanceReview,
  type PerformanceTeamMember,
  type CreatePerformanceCycleInput,
  type UpdatePerformanceCycleInput,
  type ObjectiveDefinitionInput,
  type AssessmentInput,
  type GeneralAssessmentManagerAxesInput,
  type GeneralAssessmentReferentialProfile,
  type ProgressUpdateInput,
  type ObjectiveActionInput,
  type ObjectiveActionStatus,
  type OkrImportResult,
  type GeneralAssessmentImportResult,
  type RoleProfile
} from '../domain/performance';
import type { Team, CreateTeamInput, UpdateTeamInput, RosterUser } from '../domain/team';
import type { HourlyRate } from '../domain/hourlyRates';
import type { WipSettings, WipWeeklyData } from '../domain/wipChart';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance
// Session : cookie HttpOnly envoyé par le navigateur (withCredentials), plus de jeton en localStorage.
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Session absente, expirée ou révoquée : retour à la connexion
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface TimeTrackingConfig {
  workingHoursPerDay: number;
  workingDaysPerWeek: number;
}

// Jira API - Only keeping what's used
export const jiraApi = {
  getProjects: async (): Promise<ApiResponse<JiraProject[]> & { configuredProjects?: string[] }> => {
    const { data } = await api.get('/jira/projects');
    return data;
  },
  getConfiguredBoards: async (): Promise<{ success: boolean; boards: JiraBoard[] }> => {
    const { data } = await api.get('/jira/configured-boards');
    return data;
  },
  getTimeConfig: async (): Promise<{ success: boolean } & TimeTrackingConfig> => {
    const { data } = await api.get('/jira/time-config');
    return data;
  },
  getClaudeUsStats: async (
    quarter: ClaudeUsQuarter,
    year: number = new Date().getFullYear()
  ): Promise<{ success: boolean } & ClaudeUsStats> => {
    const { data } = await api.get('/jira/claude-us-stats', { params: { quarter, year } });
    return data;
  },
  getClaudeUsIssues: async (params: ClaudeUsIssuesParams): Promise<{ success: boolean } & ClaudeUsIssuesResponse> => {
    const { data } = await api.get('/jira/claude-us-issues', { params });
    return data;
  }
};

export type ClaudeUsBasis = 'done' | 'created';

/** Détail des US Claude d'une série. */
export interface ClaudeUsIssuesParams {
  quarter: ClaudeUsQuarter;
  year: number;
  basis: ClaudeUsBasis;
  /** Restreint au board (équipe) ; absent = tous les boards. */
  boardId?: number;
}

/** Ligne du détail des US Claude. */
export interface ClaudeUsIssueRow {
  key: string;
  summary: string;
  status: string;
  created: string | null;
  /** Date de résolution Jira, sinon date de passage à Done. */
  resolved: string | null;
  /** Date d'ajout du label Claude, lue dans l'historique Jira. */
  labelAddedAt: string | null;
}

export interface ClaudeUsIssuesResponse {
  jql: string;
  label: string;
  issues: ClaudeUsIssueRow[];
}

export type ClaudeUsQuarter = 'all' | 'Q1' | 'Q2' | 'Q3' | 'Q4';

export interface ClaudeUsCounts {
  claudeCount: number;
  nonClaudeCount: number;
  totalCount: number;
  claudePercent: number;
}

/** Une série d'indicateurs US Claude (US terminées ou US créées sur la période). */
export interface ClaudeUsSection extends ClaudeUsCounts {
  /** Détail par board (équipe) ; vide si repli sur le projet. */
  byTeam: Array<{ id: number; name: string } & ClaudeUsCounts>;
}

/** US Claude (label / filtre Jira) vs non Claude sur la période (issue #39). */
export interface ClaudeUsStats {
  year: number;
  quarter: ClaudeUsQuarter;
  from: string;
  toExclusive: string;
  criterion: { kind: 'label' | 'filter'; value: string };
  /** US passées à Done sur la période. */
  done: ClaudeUsSection;
  /** US créées sur la période, quel que soit leur statut. */
  created: ClaudeUsSection;
}

export interface JiraBoard {
  id: number;
  name: string;
  projectKey: string | null;
}

export interface EpicProgressItem {
  epicKey: string;
  summary: string;
  issueType: string;
  status: string;
  statusCategoryKey: string | null;
  childIssueCount: number;
  originalEstimateSeconds: number;
  timeSpentSeconds: number;
  /** Macro chiffrage (customfield_10992), en secondes, pour comparaison avec le temps passé */
  macroChiffrageSeconds?: number | null;
  totalStoryPoints: number;
  progressPercent: number;
  isOverrun: boolean;
  /** Teams associées (epic/legend + descendants) */
  teams?: string[];
  /** Agrégats US enfants (backend KPI) — optionnels pour rétrocompat API */
  ticketsDone?: number;
  ticketsTodo?: number;
  ticketsInProgress?: number;
  storyPointsDone?: number;
  storyPointsTodo?: number;
  storyPointsInProgress?: number;
}

export interface EpicProgressResponse {
  boardId: number;
  boardName: string;
  projectKey: string | null;
  epicCount: number;
  total: number;
  page: number;
  pageSize: number;
  epics: EpicProgressItem[];
}

export type EpicTypeFilter = 'all' | 'epic' | 'legend';

/** Filtre par statut : Terminées (done), À faire (new), En cours (indeterminate) */
export type EpicStatusFilter = 'all' | 'done' | 'new' | 'indeterminate';

export interface EpicSearchItem {
  epicKey: string;
  summary: string;
  issueType: string;
  status: string;
  statusCategoryKey: string | null;
}

export interface EpicSearchResponse {
  boardId: number;
  query: string;
  results: EpicSearchItem[];
}

export interface EpicChildIssue {
  issueKey: string;
  summary: string;
  issueType: string;
  status: string;
  statusCategoryKey: string | null;
  originalEstimateSeconds: number;
  timeSpentSeconds: number;
  storyPoints: number | null;
  parentKey: string | null;
  hierarchyLevel: number;
  children?: EpicChildIssue[];
}

export interface EpicDetailsResponse {
  epicKey: string;
  summary: string;
  issueType: string;
  status: string;
  statusCategoryKey: string | null;
  originalEstimateSeconds: number;
  timeSpentSeconds: number;
  /** Macro chiffrage (customfield_10992), en secondes */
  macroChiffrageSeconds?: number | null;
  totalStoryPoints: number;
  progressPercent: number;
  isOverrun: boolean;
  children: EpicChildIssue[];
}

export const epicApi = {
  getProgress: async (
    boardId: number,
    typeFilter: EpicTypeFilter = 'all',
    statusFilter: EpicStatusFilter = 'all',
    page: number = 1,
    pageSize: number = 20,
    summaryPrefix?: string
  ): Promise<{ success: boolean } & EpicProgressResponse> => {
    const params: Record<string, unknown> = { boardId, typeFilter, statusFilter, page, pageSize };
    if (summaryPrefix && summaryPrefix !== 'all') params.summaryPrefix = summaryPrefix;
    const { data } = await api.get('/jira/epic-progress', { params });
    return data;
  },
  
  search: async (boardId: number, query: string, typeFilter: EpicTypeFilter = 'all', statusFilter: EpicStatusFilter = 'all'): Promise<{ success: boolean } & EpicSearchResponse> => {
    const { data } = await api.get('/jira/epic-search', {
      params: { boardId, query, typeFilter, statusFilter }
    });
    return data;
  },
  
  getDetails: async (epicKey: string): Promise<{ success: boolean } & EpicDetailsResponse> => {
    const { data } = await api.get(`/jira/epic/${epicKey}/details`);
    return data;
  },

  getTimeByUser: async (epicKey: string): Promise<{ success: boolean } & EpicTimeByUserResponse> => {
    const { data } = await api.get(`/jira/epic/${epicKey}/time-by-user`);
    return data;
  }
};

/** Temps passé par une personne sur l'ensemble des tickets d'une épic (issue #44). */
export interface EpicTimeByUserRow {
  accountId: string;
  displayName: string;
  avatarUrl: string | null;
  /** Poste = nom du rôle de l'utilisateur de l'app (null si non retrouvé). */
  role: string | null;
  timeSpentSeconds: number;
  percent: number;
  worklogCount: number;
  issueCount: number;
  firstWorklogAt: string | null;
  lastWorklogAt: string | null;
  /** Présents seulement pour le super admin et les rôles avec accès aux coûts. */
  hourlyRates?: HourlyRate[];
  cost?: number | null;
}

/** Temps passé par rôle (poste) sur une épic ; role null = poste non renseigné. */
export interface EpicTimeByRoleRow {
  role: string | null;
  timeSpentSeconds: number;
  percent: number;
  peopleCount: number;
  /** Présents seulement avec l'accès aux coûts. */
  cost?: number | null;
  peopleWithoutCost?: number;
}

export interface EpicTimeByUserResponse {
  epicKey: string;
  issueCount: number;
  totalSeconds: number;
  people: EpicTimeByUserRow[];
  byRole: EpicTimeByRoleRow[];
  /** Coût total, visible par tous. Absent des réponses mises en cache avant ce changement. */
  totalCost?: number;
  peopleWithoutCost?: number;
}

// Sync API
export const syncApi = {
  // Force sync all data from Jira (with extended timeout)
  forceSync: async (): Promise<{ 
    success: boolean; 
    message: string; 
    projectsSynced?: number; 
    totalProjects?: number 
  }> => {
    const { data } = await api.post('/worklog/sync', {}, { timeout: 120000 }); // 2 minutes timeout
    return data;
  },

  // Clear cache
  clearCache: async (): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.delete('/worklog/cache');
    return data;
  }
};

// Support Snapshot types
export interface SupportSnapshotSummary {
  id: string;
  sprintName: string;
  savedAt: string;
  savedBy: {
    id: string;
    email: string;
    name?: string;
  };
  dateRange: {
    from: string;
    to: string;
  };
  notes?: string;
  summary: {
    totalTickets: number;
    resolvedTickets: number;
    totalPonderation: number;
    resolvedPonderation: number;
  };
}

export interface SupportSnapshotFull extends Omit<SupportSnapshotSummary, 'summary'> {
  statusCounts: {
    total: number;
    todo: number;
    inProgress: number;
    qa: number;
    resolved: number;
  };
  ponderationByStatus: {
    total: number;
    todo: number;
    inProgress: number;
    qa: number;
    resolved: number;
  };
  ponderationByType: Record<string, number>;
  ponderationByAssignee: Array<{
    assignee: string;
    ponderation: number;
    ticketCount: number;
  }>;
  ponderationByLevel: {
    low: { count: number; total: number };
    medium: { count: number; total: number };
    high: { count: number; total: number };
    veryHigh: { count: number; total: number };
  };
  ponderationByLabel: Array<{
    label: string;
    ponderation: number;
    ticketCount: number;
  }>;
  ponderationByTeam: Array<{
    team: string;
    ponderation: number;
    ticketCount: number;
  }>;
  backlog: {
    ticketCount: number;
    totalPonderation: number;
  };
  avgResolutionTimeHours: number;
  avgFirstResponseTimeHours: number;
  avgResolutionTimeFromDatesHours: number;
  highPondFastResolutionPercent: number;
  veryHighPondFastResolutionPercent: number;
  totalPonderation: number;
}

// Support Snapshot API
export const supportSnapshotApi = {
  // Sauvegarder un snapshot
  saveSnapshot: async (sprintName: string, notes?: string): Promise<{ success: boolean; snapshot?: { id: string }; message?: string }> => {
    const { data } = await api.post('/worklog/support-snapshot?activeSprint=true', {
      sprintName,
      notes
    });
    return data;
  },

  // Lister tous les snapshots
  getSnapshots: async (limit = 50): Promise<{ success: boolean; snapshots: SupportSnapshotSummary[] }> => {
    const { data } = await api.get(`/worklog/support-snapshots?limit=${limit}`);
    return data;
  },

  // Récupérer un snapshot complet
  getSnapshot: async (id: string): Promise<{ success: boolean; snapshot: SupportSnapshotFull }> => {
    const { data } = await api.get(`/worklog/support-snapshot/${id}`);
    return data;
  },

  // Supprimer un snapshot
  deleteSnapshot: async (id: string): Promise<{ success: boolean; message?: string }> => {
    const { data } = await api.delete(`/worklog/support-snapshot/${id}`);
    return data;
  }
};

// Dashboard Snapshot types
export interface DashboardSnapshotSummary {
  id: string;
  sprintName: string;
  savedAt: string;
  savedBy: {
    id: string;
    email: string;
    name?: string;
  };
  dateRange: {
    from: string;
    to: string;
  };
  notes?: string;
  summary: {
    totalTickets: number;
    resolvedTickets: number;
    totalPoints: number;
    resolvedPoints: number;
    totalTimeHours: number;
  };
}

// Stats can be for project (legacy) or board (new)
export interface ProjectStats {
  key?: string; // Legacy project key
  boardId?: number; // New board ID
  name: string;
  projectKey?: string | null; // Project key associated with board
  color: string;
  totalPoints: number;
  todoPoints: number;
  inProgressPoints: number;
  qaPoints: number;
  resolvedPoints: number;
  estimatedPoints: number;
  totalTickets: number;
  todoTickets: number;
  inProgressTickets: number;
  qaTickets: number;
  resolvedTickets: number;
  totalTimeHours: number;
  backlogTickets: number;
  backlogPoints: number;
}

export interface DashboardTotals {
  totalPoints: number;
  todoPoints: number;
  inProgressPoints: number;
  qaPoints: number;
  resolvedPoints: number;
  estimatedPoints: number;
  totalTickets: number;
  todoTickets: number;
  inProgressTickets: number;
  qaTickets: number;
  resolvedTickets: number;
  totalTimeHours: number;
  backlogTickets: number;
  backlogPoints: number;
}

export interface DashboardSnapshotFull extends Omit<DashboardSnapshotSummary, 'summary'> {
  projectsStats: ProjectStats[];
  totals: DashboardTotals;
}

// Dashboard Snapshot API
export const dashboardSnapshotApi = {
  // Sauvegarder un snapshot - accepts both ProjectStats (legacy) and BoardStats (new)
  saveSnapshot: async (
    sprintName: string, 
    projectsStats: Array<Partial<ProjectStats> & { name: string; color: string }>, 
    totals: DashboardTotals,
    dateRange: { from: string; to: string },
    notes?: string
  ): Promise<{ success: boolean; snapshot?: { id: string }; message?: string }> => {
    // Backend schema requires "key" (string) per project; frontend may send boardId only
    const normalizedStats = projectsStats.map((p) => ({
      ...p,
      key: p.key ?? String((p as { boardId?: number }).boardId ?? (p as { projectKey?: string }).projectKey ?? p.name)
    }));
    const { data } = await api.post('/jira/dashboard-snapshot', {
      sprintName,
      projectsStats: normalizedStats,
      totals,
      dateRange,
      notes
    });
    return data;
  },

  // Lister tous les snapshots
  getSnapshots: async (limit = 50): Promise<{ success: boolean; snapshots: DashboardSnapshotSummary[] }> => {
    const { data } = await api.get(`/jira/dashboard-snapshots?limit=${limit}`);
    return data;
  },

  // Récupérer un snapshot complet
  getSnapshot: async (id: string): Promise<{ success: boolean; snapshot: DashboardSnapshotFull }> => {
    const { data } = await api.get(`/jira/dashboard-snapshot/${id}`);
    return data;
  },

  // Supprimer un snapshot
  deleteSnapshot: async (id: string): Promise<{ success: boolean; message?: string }> => {
    const { data } = await api.delete(`/jira/dashboard-snapshot/${id}`);
    return data;
  }
};

// Brevo (Marketing) API
export interface BrevoList {
  id: number;
  name: string;
  totalSubscribers: number;
  totalBlacklisted: number;
  uniqueSubscribers: number;
}

export interface BrevoCampaignStats {
  sent?: number;
  delivered?: number;
  opened?: number;
  clicked?: number;
  unsubscribed?: number;
  hardBounces?: number;
  softBounces?: number;
}

export interface BrevoCampaign {
  id: number;
  name: string;
  subject?: string;
  type: string;
  status: string;
  scheduledAt?: string;
  sentDate?: string;
  statistics?: BrevoCampaignStats;
}

export interface BrevoStats {
  contactsCount: number;
  listsCount: number;
  totalSubscribers: number;
  lists: BrevoList[];
  recentCampaigns: BrevoCampaign[];
  manualCampaigns?: BrevoCampaign[];
}

export type BrevoTransactionalEventType =
  | 'requests'
  | 'delivered'
  | 'hardBounces'
  | 'softBounces'
  | 'bounces'
  | 'opened'
  | 'clicks'
  | 'spam'
  | 'invalid'
  | 'deferred'
  | 'blocked'
  | 'unsubscribed'
  | 'error'
  | 'loadedByProxy';

export interface BrevoTransactionalEvent {
  date: string;
  email: string;
  event: BrevoTransactionalEventType;
  messageId: string;
  subject?: string;
  tag?: string;
  templateId?: number;
  from?: string;
  ip?: string;
  link?: string;
  reason?: string;
}

export const brevoApi = {
  getStatus: async (): Promise<{ success: boolean; configured: boolean }> => {
    const { data } = await api.get('/brevo/status');
    return data;
  },
  getAccount: async (): Promise<{ success: boolean; account?: Record<string, unknown> }> => {
    const { data } = await api.get('/brevo/account');
    return data;
  },
  getStats: async (): Promise<{ success: boolean; stats?: BrevoStats; brevoAuthFailed?: boolean }> => {
    const { data } = await api.get('/brevo/stats');
    return data;
  },
  getTransactionalEvents: async (params?: { days?: number; limit?: number; event?: string }): Promise<{ success: boolean; events?: BrevoTransactionalEvent[] }> => {
    const { data } = await api.get('/brevo/transactional/events', { params });
    return data;
  },
  /** Export liste des emails (ayant cliqué ou désinscrit) pour une campagne. Peut prendre 30–60 s. */
  getCampaignRecipients: async (
    campaignId: number,
    type: 'clickers' | 'unsubscribed'
  ): Promise<{ success: boolean; emails?: string[] }> => {
    const { data } = await api.get(`/brevo/campaigns/${campaignId}/recipients`, {
      params: { type },
      timeout: 90000
    });
    return data;
  }
};

// Monday.com (Produit) API
export interface MondayUser {
  id: number;
  name: string;
  email?: string;
}

export interface MondayBoard {
  id: string;
  name: string;
  state?: string;
  boardKind?: string;
  itemCount?: number;
  workspaceId?: string;
}

export interface MondayWorkspace {
  id: string;
  name: string;
  kind?: string;
}

export interface MondayColumn {
  id: string;
  title: string;
  type: string;
}

export interface MondayItem {
  id: string;
  name: string;
  column_values?: Array<{ id: string; text?: string; type: string; value?: string }>;
}

/** Vue d'un board Monday (filtres, paramètres, tri). */
export interface MondayBoardView {
  id: string;
  name: string;
  type: string;
  filter?: unknown;
  settings?: unknown;
  sort?: unknown;
  tags?: string[];
  filterUserId?: number;
  filterTeamId?: number;
}

export const mondayApi = {
  getStatus: async (): Promise<{ success: boolean; configured: boolean }> => {
    const { data } = await api.get('/monday/status');
    return data;
  },
  getMe: async (): Promise<{ success: boolean; me?: MondayUser }> => {
    const { data } = await api.get('/monday/me');
    return data;
  },
  getWorkspaces: async (): Promise<{ success: boolean; workspaces?: MondayWorkspace[] }> => {
    const { data } = await api.get('/monday/workspaces');
    return data;
  },
  getBoards: async (
    limit = 100,
    workspaceIds?: string[]
  ): Promise<{ success: boolean; boards?: MondayBoard[] }> => {
    const params: { limit: number; workspace_ids?: string } = { limit };
    if (workspaceIds?.length) params.workspace_ids = workspaceIds.join(',');
    const { data } = await api.get('/monday/boards', { params });
    return data;
  },
  getBoard: async (
    boardId: string,
    itemsLimit = 100
  ): Promise<{
    success: boolean;
    board?: MondayBoard;
    columns?: MondayColumn[];
    items?: MondayItem[];
  }> => {
    const { data } = await api.get(`/monday/boards/${boardId}`, {
      params: { itemsLimit },
    });
    return data;
  },
  /** Récupère les vues et filtres Monday d'un board (API 2025-10+). */
  getBoardViews: async (
    boardId: string
  ): Promise<{ success: boolean; views?: MondayBoardView[] }> => {
    const { data } = await api.get(`/monday/boards/${boardId}/views`);
    return data;
  },
};

/** Point hebdo sprint : document de réunion partagé et éditable. */
export const meetingApi = {
  list: async (
    limit = 30
  ): Promise<{ success: boolean; count: number; meetings: WeeklyMeetingSummary[] }> => {
    const { data } = await api.get('/meetings', { params: { limit } });
    return data;
  },
  getLatest: async (): Promise<{ success: boolean; meeting: WeeklyMeeting }> => {
    const { data } = await api.get('/meetings/latest');
    return data;
  },
  getById: async (id: string): Promise<{ success: boolean; meeting: WeeklyMeeting }> => {
    const { data } = await api.get(`/meetings/${id}`);
    return data;
  },
  create: async (date?: string): Promise<{ success: boolean; meeting: WeeklyMeeting }> => {
    const { data } = await api.post('/meetings', date ? { date } : {});
    return data;
  },
  /** Crée le point suivant en reconduisant structure, actions, blocages et interactions ouverts. */
  createNext: async (
    id: string,
    date?: string
  ): Promise<{ success: boolean; meeting: WeeklyMeeting }> => {
    const { data } = await api.post(`/meetings/${id}/next`, date ? { date } : {});
    return data;
  },
  /**
   * `origin` : identifiant d'onglet du client (voir PointHebdoPage), renvoyé tel quel dans
   * l'événement socket `meeting:update` pour que l'auteur de la modification ignore son
   * propre écho.
   */
  update: async (
    id: string,
    patch: WeeklyMeetingPatch,
    origin?: string
  ): Promise<{ success: boolean; meeting: WeeklyMeeting }> => {
    const { data } = await api.patch(`/meetings/${id}`, patch, {
      headers: origin ? { 'X-Client-Origin': origin } : undefined,
    });
    return data;
  },
  remove: async (id: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.delete(`/meetings/${id}`);
    return data;
  },
};

export const performanceApi = {
  getCycles: async (): Promise<{ success: boolean; cycles: PerformanceCycle[] }> => {
    const { data } = await api.get('/performance/cycles');
    return data;
  },
  createCycle: async (
    input: CreatePerformanceCycleInput
  ): Promise<{ success: boolean; cycle: PerformanceCycle }> => {
    const { data } = await api.post('/performance/cycles', input);
    return data;
  },
  updateCycle: async (
    id: string,
    patch: UpdatePerformanceCycleInput
  ): Promise<{ success: boolean; cycle: PerformanceCycle }> => {
    const { data } = await api.patch(`/performance/cycles/${id}`, patch);
    return data;
  },
  /** Ma fiche de performance pour un cycle (le cycle actif par défaut, créée à la volée si besoin). */
  getMyReview: async (cycleId?: string): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.get('/performance/reviews/me', { params: cycleId ? { cycleId } : undefined });
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /** Ajoute une mise à jour d'avancement à un KR de ma fiche (verrou optimiste géré côté serveur, 409 si conflit). */
  updateKeyResultProgress: async (
    objectiveId: string,
    krId: string,
    input: ProgressUpdateInput & { cycleId?: string }
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.post(
      `/performance/reviews/me/objectives/${objectiveId}/krs/${krId}/progress`,
      input
    );
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /** Le collaborateur change le statut d'une de ses actions à mener (à faire / en cours / terminé). */
  updateMyActionStatus: async (
    objectiveId: string,
    actionId: string,
    status: ObjectiveActionStatus
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.patch(
      `/performance/reviews/me/objectives/${objectiveId}/actions/${actionId}/status`,
      { status }
    );
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /** Lead/CTO : ajoute une action à mener sur un objectif d'un collaborateur. */
  addObjectiveAction: async (
    userId: string,
    objectiveId: string,
    input: ObjectiveActionInput & { cycleId?: string }
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.post(`/performance/reviews/${userId}/objectives/${objectiveId}/actions`, input);
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /** Lead/CTO : modifie une action (libellé, échéance, statut). */
  updateObjectiveAction: async (
    userId: string,
    objectiveId: string,
    actionId: string,
    input: ObjectiveActionInput & { cycleId?: string }
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.patch(
      `/performance/reviews/${userId}/objectives/${objectiveId}/actions/${actionId}`,
      input
    );
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /** Lead/CTO : supprime une action. */
  deleteObjectiveAction: async (
    userId: string,
    objectiveId: string,
    actionId: string
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.delete(
      `/performance/reviews/${userId}/objectives/${objectiveId}/actions/${actionId}`
    );
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /** Auto-évaluation sur ma propre fiche (jamais sur celle d'un autre collaborateur). */
  updateSelfAssessment: async (
    input: AssessmentInput & { cycleId?: string }
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.patch('/performance/reviews/me/self-assessment', input);
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /** Fiches dans la portée de l'acteur (CTO : toutes, filtrables par équipe ; lead : ses équipes). */
  listReviews: async (params?: {
    cycleId?: string;
    teamId?: string;
    status?: string;
  }): Promise<{ success: boolean; reviews: PerformanceReview[] }> => {
    const { data } = await api.get('/performance/reviews', { params });
    return { ...data, reviews: (data.reviews ?? []).map(normalizePerformanceReview) };
  },
  getReview: async (
    userId: string,
    cycleId?: string
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.get(`/performance/reviews/${userId}`, {
      params: cycleId ? { cycleId } : undefined
    });
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /** (Re)définit les objectifs d'un collaborateur — fusion par id côté serveur, préserve l'avancement déjà saisi. */
  defineObjectives: async (
    userId: string,
    objectives: ObjectiveDefinitionInput[],
    cycleId?: string
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.patch(`/performance/reviews/${userId}/objectives`, { objectives, cycleId });
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  updateManagerAssessment: async (
    userId: string,
    input: AssessmentInput & { cycleId?: string }
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.patch(`/performance/reviews/${userId}/manager-assessment`, input);
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /** Clôture explicite du semestre — seul passage à "complete". */
  completeReview: async (
    userId: string,
    cycleId?: string
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.patch(`/performance/reviews/${userId}/complete`, cycleId ? { cycleId } : {});
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /**
   * Évaluation manager sur la grille générale (4 axes × sous-critères) — le manager choisit une
   * réponse verbeuse par sous-critère (`axes`), jamais une note brute : le score est résolu
   * serveur à partir du référentiel du profil de poste ciblé (`roleProfile`, mémorisé sur la
   * fiche s'il est fourni). Voir `getGeneralAssessmentReferential` pour charger les référentiels.
   */
  updateGeneralManagerAssessment: async (
    userId: string,
    input: { axes: GeneralAssessmentManagerAxesInput; roleProfile?: RoleProfile; cycleId?: string }
  ): Promise<{ success: boolean; review: PerformanceReview }> => {
    const { data } = await api.patch(`/performance/reviews/${userId}/general-manager-assessment`, input);
    return { ...data, review: normalizePerformanceReview(data.review) };
  },
  /** Référentiels de notation détaillée (un par profil de poste), pour le formulaire de notation manager. */
  getGeneralAssessmentReferential: async (): Promise<{
    success: boolean;
    profiles: GeneralAssessmentReferentialProfile[];
  }> => {
    const { data } = await api.get('/performance/general-assessment-referential');
    return data;
  },
  /** Membres d'équipe dans la portée de l'acteur, avec ou sans fiche de performance ouverte. */
  getTeamMembers: async (params?: {
    teamId?: string;
  }): Promise<{ success: boolean; members: PerformanceTeamMember[] }> => {
    const { data } = await api.get('/performance/team-members', { params });
    return data;
  },
  /** Import fichiers d'entretien (session courante, rien n'est persisté hors objectifs). */
  importOkr: async (input: {
    files: File[];
    cycleId: string;
    dryRun: boolean;
  }): Promise<OkrImportResult> => {
    const form = new FormData();
    form.append('cycleId', input.cycleId);
    form.append('dryRun', String(input.dryRun));
    input.files.forEach((file) => form.append('files', file));
    const { data } = await api.post('/performance/import-okr', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      transformRequest: [
        (body, headers) => {
          if (body instanceof FormData) {
            delete headers['Content-Type'];
          }
          return body;
        }
      ]
    });
    return data;
  },
  /** Import grilles d'auto-évaluation individuelle (session courante, rien n'est persisté hors notes). */
  importGeneralAssessment: async (input: {
    files: File[];
    cycleId: string;
    dryRun: boolean;
  }): Promise<GeneralAssessmentImportResult> => {
    const form = new FormData();
    form.append('cycleId', input.cycleId);
    form.append('dryRun', String(input.dryRun));
    input.files.forEach((file) => form.append('files', file));
    const { data } = await api.post('/performance/import-general-assessment', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      transformRequest: [
        (body, headers) => {
          if (body instanceof FormData) {
            delete headers['Content-Type'];
          }
          return body;
        }
      ]
    });
    return data;
  }
};

export const teamApi = {
  list: async (): Promise<{ success: boolean; teams: Team[] }> => {
    const { data } = await api.get('/teams');
    return data;
  },
  /** Tous les collaborateurs actifs (avec ou sans équipe) — réservé à la gestion d'équipes (CTO/admin). */
  getRoster: async (): Promise<{ success: boolean; users: RosterUser[] }> => {
    const { data } = await api.get('/teams/roster');
    return data;
  },
  create: async (input: CreateTeamInput): Promise<{ success: boolean; team: Team }> => {
    const { data } = await api.post('/teams', input);
    return data;
  },
  update: async (id: string, patch: UpdateTeamInput): Promise<{ success: boolean; team: Team }> => {
    const { data } = await api.patch(`/teams/${id}`, patch);
    return data;
  },
  /** Rattache (`teamId`) ou détache (`teamId: null`) un collaborateur à une équipe. */
  assignMember: async (
    userId: string,
    teamId: string | null
  ): Promise<{ success: boolean; user: { id: string; teamId: string | null } }> => {
    const { data } = await api.patch(`/teams/members/${userId}`, { teamId });
    return data;
  },
  setDelegation: async (
    userId: string,
    canManageTeamAssignment: boolean
  ): Promise<{ success: boolean; user: { id: string; canManageTeamAssignment: boolean } }> => {
    const { data } = await api.patch(`/teams/members/${userId}/delegation`, { canManageTeamAssignment });
    return data;
  }
};

// Organisation — paramètres et durées de conservation (super admin)
export interface RetentionSettings {
  /** Logs d'activité supprimés au-delà de N mois (null = pas de purge). */
  activityLogMonths: number | null;
  /** Fiches de performance supprimées N années après la fin du cycle (null = pas de purge). */
  performanceReviewYears: number | null;
  /** Comptes désactivés anonymisés après N mois (null = jamais automatiquement). */
  inactiveAccountMonths: number | null;
}

export interface OrganizationSettings {
  name: string;
  slug: string;
  allowedEmailDomains: string[];
  allowLocalAccounts: boolean;
  retention: RetentionSettings;
}

export const organizationApi = {
  async getMine(): Promise<OrganizationSettings> {
    const { data } = await api.get('/organizations/me');
    return data.organization;
  },
  async updateRetention(retention: Partial<RetentionSettings>): Promise<OrganizationSettings> {
    const { data } = await api.patch('/organizations/me/retention', retention);
    return data.organization;
  }
};

export default api;

/** Utilisateur de la page « Coûts horaires » (issue #44). */
export interface CostUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  provider: string;
  isActive: boolean;
  roleName: string | null;
  /** Coût initial puis changements datés (vide si aucun coût). */
  hourlyRates: HourlyRate[];
  /** Compte non SSO ajouté manuellement par un super admin. */
  manual: boolean;
}

// Coûts horaires : comptes SSO + comptes non SSO ajoutés par un super admin (issue #44)
export const costsApi = {
  /** Liste des coûts ; `canManage` = l'appelant (super admin) peut ajouter / retirer des comptes non SSO. */
  getUsers: async (): Promise<{ success: boolean; users: CostUser[]; canManage: boolean }> => {
    const { data } = await api.get('/costs/users');
    return data;
  },
  /** Remplace les coûts horaires (€) : coût initial puis jusqu'à deux changements datés ; [] pour effacer. */
  updateHourlyRates: async (userId: string, hourlyRates: HourlyRate[]): Promise<{ success: boolean; hourlyRates: HourlyRate[] }> => {
    const { data } = await api.patch(`/costs/users/${userId}`, { hourlyRates });
    return data;
  },
  /** Comptes non SSO pas encore dans la liste (super admin). */
  getCandidates: async (): Promise<{ success: boolean; users: CostUser[] }> => {
    const { data } = await api.get('/costs/candidates');
    return data;
  },
  /** Ajoute un compte non SSO à la liste (super admin). */
  addUser: async (userId: string): Promise<{ success: boolean }> => {
    const { data } = await api.post(`/costs/users/${userId}`);
    return data;
  },
  /** Retire un compte non SSO ajouté ; son coût horaire est effacé (super admin). */
  removeUser: async (userId: string): Promise<{ success: boolean }> => {
    const { data } = await api.delete(`/costs/users/${userId}`);
    return data;
  },
};

/** Page « Suivi WIP » (issue #79) : WIP hebdomadaire et réglages partagés des seuils. */
export const wipApi = {
  /** WIP et terminés par semaine (lundi → dimanche) ; `canEdit` = super admin. */
  getWeekly: async (from: string, to: string): Promise<{ success: boolean; data: WipWeeklyData; canEdit: boolean }> => {
    // Lecture de l'historique des statuts : peut dépasser le délai par défaut sur de longues périodes.
    const { data } = await api.get('/wip/weekly', { params: { from, to }, timeout: 120000 });
    return data;
  },
  /** Remplace les réglages (super admin). */
  saveSettings: async (settings: WipSettings): Promise<{ success: boolean; settings: WipSettings }> => {
    const { data } = await api.put('/wip/settings', settings);
    return data;
  },
};
